import { passwordSchema } from '@napaicp/schemas'
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Label,
  PasswordInput,
} from '@napaicp/ui'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { useCompleteForcedPasswordChange } from '@/features/session/use-session'
import { apiErrorMessage } from '@/lib/api-error-message'

type ForcedPasswordChangeForm = { newPassword: string }
const formSchema = z.object({ newPassword: passwordSchema })

/**
 * BR-082/BR-083/API-050/API-051/DM-028/SEC-019/SEC-023: a session minted
 * from a Judge Temporary Initial Password is restricted server-side until
 * this completes — every protected API call fails with
 * `PASSWORD_CHANGE_REQUIRED` until then (`middleware/authorize.ts`). This
 * screen is `RootLayout`'s replacement for `<Outlet />` while
 * `session.user.passwordChangeRequired` is true, so it is the only thing a
 * restricted account can reach besides the header's own sign-out control
 * (which stays visible since `RootLayout` still renders around this).
 *
 * On success the mutation invalidates the session query; the server has
 * already rotated the session cookie (SEC-023), so the re-fetch comes back
 * with `passwordChangeRequired: false` and `RootLayout` swaps back to the
 * real `<Outlet />` on its own — no navigation call needed here.
 */
export function ForcedPasswordChangeScreen() {
  const { t } = useTranslation('common')
  const complete = useCompleteForcedPasswordChange()

  const form = useForm<ForcedPasswordChangeForm>({
    resolver: zodResolver(formSchema),
    defaultValues: { newPassword: '' },
  })

  return (
    <div className="mx-auto flex max-w-sm flex-col gap-6">
      <h1 className="sr-only">{t('forcedPasswordChange.title')}</h1>
      <Card>
        <CardHeader>
          <CardTitle>{t('forcedPasswordChange.title')}</CardTitle>
          <CardDescription>{t('forcedPasswordChange.description')}</CardDescription>
        </CardHeader>
        <CardContent>
          <form
            className="flex flex-col gap-4"
            onSubmit={(event) => {
              void form.handleSubmit((values) => complete.mutate(values))(event)
            }}
          >
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="forced-password-change-new">
                {t('forcedPasswordChange.newPassword')}
              </Label>
              <PasswordInput
                id="forced-password-change-new"
                autoComplete="new-password"
                showPasswordLabel={t('passwordVisibility.show')}
                hidePasswordLabel={t('passwordVisibility.hide')}
                disabled={complete.isPending || complete.isSuccess}
                {...form.register('newPassword')}
              />
              {form.formState.errors.newPassword ? (
                <p className="text-sm text-destructive">
                  {form.formState.errors.newPassword.message}
                </p>
              ) : (
                <p className="text-sm text-muted-foreground">
                  {t('forcedPasswordChange.passwordHint')}
                </p>
              )}
            </div>

            {complete.isError ? (
              <p className="text-sm text-destructive">
                {apiErrorMessage(complete.error, t, t('forcedPasswordChange.failed'))}
              </p>
            ) : null}

            {complete.isSuccess ? (
              <p className="text-sm text-primary">{t('forcedPasswordChange.success')}</p>
            ) : (
              <Button type="submit" disabled={complete.isPending}>
                {t('forcedPasswordChange.action')}
              </Button>
            )}
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
